import * as React from 'react';
import {GridColorPicker} from '@deviceinsight/ng-ui-components';
import {Checkbox} from '@deviceinsight/ng-ui-basic-components';
import type {DeviceElementTarget} from '../../../modules/ruleengine/types';
import {
	BLINKING_FREQUENCY,
	COLOR_PALETTE,
	flashingCheckBoxMessage,
	GPIO_ACTION_VALUE,
	ValueType
} from '../../../modules/ruleengine/config/consts';
import GpioToggleButton from '../../GpioToggleButton';
type SelectorProps = {
	element: DeviceElementTarget;
	value: string | null | undefined;
	onValueSelect: (value: string | null | undefined) => void;
	canEdit: boolean;
};
type Props = SelectorProps & {
	frequency: number | null | undefined;
	onFrequencyChange: (frequency: number | null | undefined) => void;
};
type FrequencyProps = {
	element: DeviceElementTarget;
	frequency: number | null | undefined;
	onFrequencyChange: (frequency: number | null | undefined) => void;
	canEdit: boolean;
};
type GpioStringToggleProps = {
	value: string | null | undefined;
	onValueSelect: (value: string | null | undefined) => void;
	canEdit: boolean;
};

const GpioStringToggleButton = ({value, onValueSelect, canEdit}: GpioStringToggleProps) => {
	const booleanValue = value === GPIO_ACTION_VALUE.HIGH;

	const onBoolValueChange = (bool?: boolean) => onValueSelect(bool ? GPIO_ACTION_VALUE.HIGH : GPIO_ACTION_VALUE.LOW);

	return <GpioToggleButton status={booleanValue} onChange={onBoolValueChange} canEdit={canEdit} />;
};

const DeviceValueSelector = ({element, value, onValueSelect, canEdit}: SelectorProps) => {
	switch (element.valueType) {
		case ValueType.color:
			return (
				<span data-testid="color-selector">
					<GridColorPicker
						inline={false}
						colors={canEdit ? COLOR_PALETTE : value ? [value] : []}
						value={value || COLOR_PALETTE[0]}
						onPick={(color) => onValueSelect(color ? color.toUpperCase() : undefined)}
					/>
				</span>
			);

		case ValueType.outputState:
			return <GpioStringToggleButton value={value} onValueSelect={onValueSelect} canEdit={canEdit} />;

		default:
			return <span />;
	}
};

const FrequencySetter = ({element, frequency, onFrequencyChange, canEdit}: FrequencyProps) => {
	switch (element.valueType) {
		case ValueType.color:
			return (
				<span>
					<Checkbox
						value={frequency ? true : false}
						title={flashingCheckBoxMessage}
						label={flashingCheckBoxMessage}
						onChange={() => onFrequencyChange(frequency ? 0 : BLINKING_FREQUENCY)}
						disabled={!canEdit}
					/>
				</span>
			);

		case ValueType.outputState:
			return (
				<span>
					<input
						type="number"
						value={frequency || 0}
						onChange={({target: {value}}) => onFrequencyChange(value ? parseFloat(value) : undefined)}
						className="frequency-field"
						disabled={!canEdit}
					/>
					mHz
				</span>
			);

		default:
			return <span />;
	}
};

const RuleActionDeviceValue = ({element, value, frequency, onValueSelect, onFrequencyChange, canEdit}: Props) => {
	const {valueType} = element;
	return (
		<span className="action-device-value" data-testid="action-device-value">
			<DeviceValueSelector element={element} value={value} onValueSelect={onValueSelect} canEdit={canEdit} />
			{valueType !== ValueType.outputState && (
				<FrequencySetter
					frequency={frequency}
					onFrequencyChange={onFrequencyChange}
					element={element}
					canEdit={canEdit}
				/>
			)}
		</span>
	);
};

export default RuleActionDeviceValue;
